/**
 * EditCapacity — page (CES standard)
 * Slotting editor with two modes (modify with steppers, upload InstancePools
 * JSON) and two scopes (single asset, whole Outpost/order). Local real-time
 * validation with slotting-core; the critical flow is enforced by the
 * useDryRunFlow hook (MANDATORY dry run → confirmation modal → apply).
 * Data loading and the active-task parallelism rule live in useEditorSetup.
 */

import { Upload } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  computeDropletLayout,
  diffInstanceMaps,
  hasChanges,
  type InstanceMap,
} from 'slotting-core';
import { Button } from '../components/atoms/Button';
import { SegmentedControl } from '../components/molecules/SegmentedControl';
import { StepperRow } from '../components/molecules/StepperRow';
import { DiffTable } from '../components/molecules/DiffTable';
import { ResourceBar, ResourceBars } from '../components/molecules/ResourceBars';
import { CapacityVisual } from '../components/organisms/CapacityVisual';
import { ConfirmApplyModal } from '../components/organisms/ConfirmApplyModal';
import { useDryRunFlow } from '../hooks/useDryRunFlow';
import { useEditorSetup } from '../hooks/useEditorSetup';
import { useInstances } from '../hooks/useInstances';
import {
  aggregateCurrentConfig,
  buildFamilyGroups,
  familyUsage,
  parseInstancePoolsJson,
  specsForGeneration,
  validateAssetProposal,
  validateOutpostProposal,
  type TypeSpec,
} from '../services/editor';
import { findFamily } from '../services/slotting';

type Mode = 'modify' | 'json';

const VALIDATION_MESSAGE_CLASSES =
  'mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700';
const UNDER_CAPACITY_CLASSES =
  'mt-3 rounded border-l-4 border-orange-400 bg-orange-50 px-3 py-2 text-sm text-gray-700';
const FLOW_SUCCESS_CLASSES =
  'mt-4 rounded border border-green-300 bg-green-50 px-4 py-3 text-sm leading-relaxed text-gray-800';
const FLOW_ERROR_CLASSES =
  'mt-4 rounded border border-red-300 bg-red-50 px-4 py-3 text-sm leading-relaxed text-gray-800';
const INLINE_LINK_CLASSES = 'text-orange-600 underline hover:text-orange-700';

function specsLabel(spec: TypeSpec): string {
  return `${spec.vCPUs} vCPUs · ${spec.memoryGiB} GiB${spec.gpus ? ` · ${spec.gpus} GPU` : ''}`;
}

function Indicator({
  label,
  status,
  testId,
}: {
  label: string;
  status: 'ok' | 'bad' | 'neutral';
  testId: string;
}) {
  const icon = status === 'ok' ? '✅' : status === 'bad' ? '❌' : '➖';
  const color =
    status === 'bad' ? 'text-red-600' : status === 'ok' ? 'text-gray-900' : 'text-gray-500';
  return (
    <li className={`text-sm ${color}`} data-testid={testId} data-status={status}>
      <span aria-hidden="true">{icon}</span> {label}
    </li>
  );
}

export function EditCapacity({ embedded = false }: { embedded?: boolean } = {}) {
  const { outpostId = '', assetId } = useParams<{ outpostId: string; assetId?: string }>();
  const isAssetScope = assetId !== undefined;

  const { assets, supportedNames, activeTask, loading, loadError } = useEditorSetup(
    outpostId,
    assetId,
  );
  const { flow, pending, notifyEdit, runDryRun, apply } = useDryRunFlow(outpostId, { assetId });
  const { instances: runningInstances, loading: instancesLoading } = useInstances(
    outpostId,
    assetId,
  );

  const [instances, setInstances] = useState<InstanceMap>({});
  const [mode, setMode] = useState<Mode>('modify');
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [dryRunElapsed, setDryRunElapsed] = useState(0);
  const initialized = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Elapsed-seconds counter shown while the dry run is validating with AWS
  // (the call is async and can take ~20-30s). Resets whenever the flow
  // leaves the pending phase.
  useEffect(() => {
    if (flow.phase !== 'dryRunPending') {
      setDryRunElapsed(0);
      return;
    }
    setDryRunElapsed(0);
    const started = Date.now();
    const id = setInterval(() => {
      setDryRunElapsed(Math.floor((Date.now() - started) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [flow.phase]);

  const editingLocked = pending || flow.phase === 'applied';

  const asset = isAssetScope ? assets.find((a) => a.assetId === assetId) : undefined;

  // Resolve generation + family from the catalog
  const familyInfo = useMemo(() => {
    const familyName = isAssetScope
      ? asset?.instanceFamilies[0]
      : assets.find((a) => a.instanceFamilies.length > 0)?.instanceFamilies[0];
    return familyName ? findFamily(familyName) : undefined;
  }, [isAssetScope, asset, assets]);
  const generation = familyInfo?.generation;

  // Current configuration (baseline for the diff)
  const current = useMemo<InstanceMap>(() => {
    if (isAssetScope) {
      return Object.fromEntries(
        (asset?.instanceTypeCapacities ?? []).map((c) => [c.instanceType, c.count]),
      );
    }
    return aggregateCurrentConfig(assets);
  }, [isAssetScope, asset, assets]);

  // Initialize the editor once from the current configuration
  useEffect(() => {
    if (initialized.current || loading) return;
    if (isAssetScope && !asset) return;
    initialized.current = true;
    setInstances(current);
  }, [loading, isAssetScope, asset, current]);

  // Family groups the customer actually controls on this Outpost (outpost
  // scope): types per family + the capacity of the assets backing it. Never
  // bare-metal or networking catalog entries — those are not slottable.
  const groups = useMemo(
    () => (!isAssetScope && generation ? buildFamilyGroups(assets, generation) : []),
    [isAssetScope, generation, assets],
  );

  // Rows: asset scope → hardware-supported types; outpost scope → all types
  // across the family groups (JSON validation checks against this list)
  const rows = useMemo<TypeSpec[]>(() => {
    if (!generation) return [];
    if (isAssetScope) {
      const specs = specsForGeneration(generation);
      const names =
        supportedNames ?? (familyInfo ? familyInfo.familyData.sizes.map((s) => s.name) : []);
      return names
        .map((name) => specs.get(name))
        .filter((s): s is TypeSpec => s !== undefined)
        .sort((a, b) => a.vCPUs - b.vCPUs);
    }
    return groups.flatMap((group) => group.specs);
  }, [generation, isAssetScope, supportedNames, familyInfo, groups]);

  // Real-time validation
  const assetValidation = useMemo(() => {
    if (!isAssetScope || !familyInfo || !generation) return undefined;
    return validateAssetProposal(instances, familyInfo.familyData, generation, asset?.maxVcpus);
  }, [isAssetScope, familyInfo, generation, instances, asset]);

  const outpostValidation = useMemo(() => {
    if (isAssetScope || !generation) return undefined;
    return validateOutpostProposal(instances, generation, groups);
  }, [isAssetScope, generation, instances, groups]);

  const layout = useMemo(() => {
    if (!isAssetScope || !familyInfo || !generation) return undefined;
    return computeDropletLayout({ family: 'edit', instances }, familyInfo.familyData, generation);
  }, [isAssetScope, familyInfo, generation, instances]);

  const diff = useMemo(() => diffInstanceMaps(current, instances), [current, instances]);
  const valid = isAssetScope ? assetValidation?.valid === true : outpostValidation?.valid === true;
  const canRequestDryRun =
    valid &&
    hasChanges(diff) &&
    activeTask === null &&
    (flow.phase === 'editing' || flow.phase === 'dryRunFailed' || flow.phase === 'applyFailed');

  const setCount = (type: string, next: number) => {
    if (editingLocked) return;
    notifyEdit();
    setInstances((prev) => ({ ...prev, [type]: next }));
  };

  const loadJsonFromText = (text: string) => {
    if (editingLocked) return;
    const result = parseInstancePoolsJson(text);
    if (!result.ok) {
      setJsonError(result.error);
      return;
    }
    const unknown = Object.keys(result.instances).find(
      (type) => !rows.some((r) => r.name === type),
    );
    if (unknown) {
      setJsonError(`Instance type '${unknown}' is not available in this scope`);
      return;
    }
    setJsonError(null);
    notifyEdit();
    setInstances(result.instances);
    setMode('modify');
  };

  const loadJson = () => loadJsonFromText(jsonText);

  /** Read a .json file from the local machine into the editor. */
  const onFileSelected = async (file: File | undefined) => {
    if (!file || editingLocked) return;
    const text = await file.text();
    setJsonText(text);
    loadJsonFromText(text);
  };

  const resetEditor = () => {
    if (editingLocked) return;
    notifyEdit();
    setInstances(current);
  };

  if (loading) return <p className="py-8 text-center text-gray-500">Loading editor…</p>;
  if (loadError) return <p className="py-8 text-center text-red-600">{loadError}</p>;
  if (isAssetScope && !asset) {
    return <p className="py-8 text-center text-red-600">Asset {assetId} not found</p>;
  }

  const scopeLabel = isAssetScope ? `Asset ${assetId}` : 'Outpost scope (all active assets)';

  return (
    <>
      {!embedded && (
        <>
          <Link
            to={`/outposts/${outpostId}`}
            className="mb-3 inline-block text-sm text-gray-500 hover:text-orange-600"
          >
            ← Back to inventory
          </Link>
          <h2 className="mb-1 text-2xl font-bold text-gray-900">Edit capacity</h2>
        </>
      )}
      <p className="mb-6 text-sm text-gray-500" data-testid="editor-scope">
        {outpostId} · {scopeLabel}
        {isAssetScope && asset?.maxVcpus !== undefined ? ` · ${asset.maxVcpus} vCPUs max` : ''}
      </p>

      {!isAssetScope && (
        <div
          className="mb-5 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm leading-relaxed text-gray-800"
          data-testid="outpost-scope-note"
        >
          <p className="m-0 font-bold text-amber-800">
            ⚠️ Outpost-wide capacity change: affects the entire droplet farm
          </p>
          <p className="m-0 mt-1">
            AWS configures capacity automatically across all the assets of this Outpost,
            based on the possible slotting configurations and AWS best practices. You
            define the <strong>target instance pools per family</strong> and{' '}
            <strong>AWS decides which assets each pool lands on</strong>; per-droplet
            placement is not controlled from this screen. For a targeted change on one
            droplet, use the <strong>Edit</strong> button on that asset in the inventory.
          </p>
        </div>
      )}

      {activeTask && (
        <div
          className="mb-5 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm leading-relaxed text-gray-800"
          data-testid="active-task-banner"
        >
          ⚠️ Active capacity task <strong>{activeTask.capacityTaskId}</strong> (
          {activeTask.status}) on this Outpost. Only one task can run at a time, so new
          changes are blocked until it finishes.{' '}
          <Link to={`/capacity-tasks?outpostId=${outpostId}`} className={INLINE_LINK_CLASSES}>
            Open the task monitor
          </Link>
        </div>
      )}

      <div className="mb-5">
        <SegmentedControl<Mode>
          ariaLabel="Editor mode"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'modify', label: 'Modify', testId: 'mode-modify' },
            { value: 'json', label: 'Upload JSON', testId: 'mode-json' },
          ]}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_420px]">
        <section aria-label="Configuration editor">
          {mode === 'modify' ? (
            isAssetScope ? (
              <div className="divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200 bg-white shadow">
                {rows.map((spec) => (
                  <StepperRow
                    key={spec.name}
                    name={spec.name}
                    specsLabel={specsLabel(spec)}
                    count={instances[spec.name] ?? 0}
                    disabled={editingLocked}
                    onChange={(next) => setCount(spec.name, next)}
                  />
                ))}
              </div>
            ) : (
              // Outpost scope: one section per family — placement is
              // family-bound, so usage is tracked against each family's assets
              <div className="flex flex-col gap-6">
                {groups.map((group) => {
                  const usage = familyUsage(instances, group);
                  return (
                    <section
                      key={group.family}
                      aria-label={`${group.family} family`}
                      className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow"
                      data-testid={`family-group-${group.family.toLowerCase()}`}
                    >
                      <div className="border-b border-gray-200 bg-gray-50 px-5 py-3">
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-bold text-gray-900">{group.family}</h3>
                          <span className="text-xs text-gray-500">
                            {group.assetCount} asset{group.assetCount === 1 ? '' : 's'}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-col gap-1.5">
                          <ResourceBar
                            label="vCPUs"
                            used={usage.vcpusUsed}
                            total={usage.vcpuCapacity}
                            testId={`bar-vcpus-${group.family.toLowerCase()}`}
                          />
                          <ResourceBar
                            label="Memory"
                            used={usage.memoryUsed}
                            total={usage.memoryCapacity}
                            unit="GiB"
                            testId={`bar-memory-${group.family.toLowerCase()}`}
                          />
                        </div>
                      </div>
                      <div className="divide-y divide-gray-200">
                        {group.specs.map((spec) => (
                          <StepperRow
                            key={spec.name}
                            name={spec.name}
                            specsLabel={specsLabel(spec)}
                            count={instances[spec.name] ?? 0}
                            disabled={editingLocked}
                            onChange={(next) => setCount(spec.name, next)}
                          />
                        ))}
                      </div>
                    </section>
                  );
                })}
              </div>
            )
          ) : (
            <div>
              <p className="mb-3 text-sm text-gray-600">
                Load an <strong>InstancePools</strong> document (the StartCapacityTask format):
                upload a .json file from your machine or paste it below, then load it into the
                editor to review and validate before the dry run.
              </p>
              <div className="mb-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,application/json"
                  className="hidden"
                  data-testid="json-file"
                  onChange={(e) => {
                    void onFileSelected(e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
                <Button
                  variant="secondary"
                  data-testid="json-file-btn"
                  disabled={editingLocked}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <span className="flex items-center gap-2">
                    <Upload size={16} aria-hidden="true" /> Upload JSON file…
                  </span>
                </Button>
              </div>
              <label className="mb-2 block text-sm text-gray-600" htmlFor="json-input">
                Or paste the JSON here
              </label>
              <textarea
                id="json-input"
                data-testid="json-input"
                className="mb-3 w-full resize-y rounded border border-gray-300 bg-white p-3 font-mono text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500"
                rows={10}
                spellCheck={false}
                placeholder='[{"InstanceType": "m5.large", "Count": 2}]'
                value={jsonText}
                disabled={editingLocked}
                onChange={(e) => setJsonText(e.target.value)}
              />
              {jsonError && (
                <p className="mb-3 text-sm text-red-600" data-testid="json-error" role="alert">
                  {jsonError}
                </p>
              )}
              <Button
                variant="secondary"
                data-testid="json-load"
                disabled={editingLocked}
                onClick={loadJson}
              >
                Load into editor
              </Button>
            </div>
          )}
        </section>

        <aside aria-label="Validation and preview">
          {isAssetScope && layout && assetValidation && familyInfo && (
            <>
              <CapacityVisual layout={layout} />
              <ResourceBars
                memoryUsed={assetValidation.totals.memoryGiB}
                memoryTotal={familyInfo.familyData.dropletCapacity.memoryGiB}
                vcpusUsed={assetValidation.totals.vCPUs}
                vcpusTotal={asset?.maxVcpus ?? familyInfo.familyData.dropletCapacity.vCPUs}
                gpusUsed={assetValidation.totals.gpus}
                gpusTotal={
                  familyInfo.familyData.hasGPU ? familyInfo.familyData.dropletCapacity.gpus : 0
                }
              />
              <ul className="mt-4 flex flex-col gap-2">
                <Indicator
                  label="Combination possible"
                  status={assetValidation.combinationPossible ? 'ok' : 'bad'}
                  testId="val-combination"
                />
                <Indicator
                  label="Fully occupied"
                  status={assetValidation.fullyOccupied ? 'ok' : 'neutral'}
                  testId="val-fully"
                />
                <Indicator
                  label="Slots breached"
                  status={assetValidation.slotsBreached ? 'bad' : 'ok'}
                  testId="val-breach"
                />
                <Indicator
                  label="NUMA distribution"
                  status={assetValidation.numaViolation ? 'bad' : 'ok'}
                  testId="val-numa"
                />
              </ul>
              {assetValidation.message && (
                <p className={VALIDATION_MESSAGE_CLASSES} data-testid="val-message" role="alert">
                  {assetValidation.message}
                </p>
              )}
              {assetValidation.valid &&
                !assetValidation.fullyOccupied &&
                assetValidation.totals.vCPUs > 0 && (
                  <p className={UNDER_CAPACITY_CLASSES} data-testid="under-capacity">
                    ℹ️ Under capacity: this configuration does not use all available
                    capacity. AWS allows applying it as requested.
                  </p>
                )}
            </>
          )}

          {!isAssetScope && outpostValidation && (
            <>
              <p className="text-sm text-gray-600" data-testid="outpost-totals">
                Total: {outpostValidation.totalVcpus} / {outpostValidation.capacityVcpus} vCPUs
                across active assets
              </p>
              <ul className="mt-4 flex flex-col gap-2">
                <Indicator
                  label="Configuration fits active assets"
                  status={outpostValidation.valid ? 'ok' : 'bad'}
                  testId="val-outpost"
                />
              </ul>
              {outpostValidation.message && (
                <p className={VALIDATION_MESSAGE_CLASSES} data-testid="val-message" role="alert">
                  {outpostValidation.message}
                </p>
              )}
              {outpostValidation.valid &&
                outpostValidation.totalVcpus > 0 &&
                outpostValidation.totalVcpus < outpostValidation.capacityVcpus && (
                  <p className={UNDER_CAPACITY_CLASSES} data-testid="under-capacity">
                    ℹ️ Under capacity: this configuration does not use all available
                    capacity. AWS allows applying it as requested.
                  </p>
                )}
            </>
          )}

          <h3 className="mb-2 mt-6 text-base font-bold text-gray-900">
            Changes vs current configuration
          </h3>
          <DiffTable diff={diff} />

          {/* Dry-run / apply flow */}
          {flow.phase === 'dryRunPending' && (
            <div
              className="mt-4 rounded border border-orange-200 bg-orange-50 px-4 py-3"
              data-testid="dry-run-progress"
              role="status"
              aria-live="polite"
            >
              <div className="flex items-center gap-3">
                <span className="dryrun-worker text-2xl" aria-hidden="true">
                  👷
                </span>
                <div className="flex-1">
                  <p className="m-0 text-sm font-semibold text-gray-800">
                    Validating capacity plan with AWS…
                  </p>
                  <p className="m-0 text-xs text-gray-500">
                    This usually takes ~20–30s · {dryRunElapsed}s elapsed
                  </p>
                </div>
              </div>
              <div className="dryrun-progress" aria-hidden="true">
                <div className="dryrun-progress__bar" />
              </div>
            </div>
          )}
          {flow.phase === 'dryRunPassed' && (
            <div className={FLOW_SUCCESS_CLASSES} data-testid="dry-run-passed">
              ✅ Dry run passed. The configuration fits the available capacity, and editing
              again will require a new dry run.
            </div>
          )}
          {flow.phase === 'dryRunFailed' && (
            <div className={FLOW_ERROR_CLASSES} data-testid="dry-run-failed">
              ❌ Dry run failed
              {flow.dryRun?.failed ? (
                <>
                  : <strong>{flow.dryRun.failed.type}</strong> ({flow.dryRun.failed.reason})
                </>
              ) : (
                flow.error && <>: {flow.error}</>
              )}
            </div>
          )}
          {flow.phase === 'applied' && flow.applied && (
            <div className={FLOW_SUCCESS_CLASSES} data-testid="apply-success">
              ✅ Capacity task{' '}
              <strong data-testid="applied-task-id">{flow.applied.capacityTaskId}</strong> started
              (status {flow.applied.status}).{' '}
              <Link
                to={`/capacity-tasks?outpostId=${outpostId}`}
                className={INLINE_LINK_CLASSES}
                data-testid="goto-monitor"
              >
                Track its progress in the task monitor
              </Link>
            </div>
          )}
          {flow.phase === 'applyFailed' && (
            <div className={FLOW_ERROR_CLASSES} data-testid="apply-error">
              ❌ Apply failed{flow.conflict ? ' (an active capacity task already exists)' : ''}
              {flow.error ? `: ${flow.error}` : ''}
            </div>
          )}

          <div className="mt-6 flex justify-end gap-3">
            {flow.phase === 'applied' ? (
              <Link
                to={`/outposts/${outpostId}`}
                className="rounded bg-orange-500 px-4 py-2 font-semibold text-white transition-colors hover:bg-orange-600"
                data-testid="back-after-apply"
              >
                Back to inventory
              </Link>
            ) : (
              <>
                <Button
                  variant="secondary"
                  data-testid="reset-editor"
                  disabled={editingLocked}
                  onClick={resetEditor}
                >
                  Reset
                </Button>
                {(flow.phase === 'dryRunPassed' || flow.phase === 'applyFailed') && (
                  <Button data-testid="apply-btn" onClick={() => setShowModal(true)}>
                    Apply…
                  </Button>
                )}
                {flow.phase !== 'dryRunPassed' && (
                  <Button
                    data-testid="continue-dry-run"
                    disabled={!canRequestDryRun}
                    title={
                      canRequestDryRun
                        ? 'Run a dry run of this configuration'
                        : 'Requires a valid configuration with changes'
                    }
                    onClick={() => runDryRun(instances)}
                  >
                    {flow.phase === 'dryRunPending' ? 'Running dry run…' : 'Run dry run'}
                  </Button>
                )}
              </>
            )}
          </div>
        </aside>
      </div>

      {showModal && (
        <ConfirmApplyModal
          scopeLabel={`${outpostId} · ${scopeLabel}`}
          diff={diff}
          pending={flow.phase === 'applyPending'}
          instances={runningInstances}
          instancesLoading={instancesLoading}
          massive={!isAssetScope}
          onConfirm={(options) => apply(options, { onSettled: () => setShowModal(false) })}
          onCancel={() => setShowModal(false)}
        />
      )}
    </>
  );
}
